import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VirtualDataLocationDTO, VirtualLocationDTO } from '../../organisms/route-sheet/models/VirtualLocationDTO';
import { DataNamesDTO } from '../../../sections/locations-section/models/DataNamesDTO';
import { Subscription } from 'rxjs';
import { LocationsService } from '../../../sections/locations-section/services/Locations.service';
import { LocationCategoriesService } from '../../../sections/locations-section/services/LocationCategories.service';
import { VirtualLocationsService } from '../../../sections/locations-section/services/VirtualLocations.service';
import { NotificationsToastService } from 'src/app/features/shared/services/Notifications-Toast.service';
import { DataLocationDTO } from '../../../sections/locations-section/models/LocationDTO';
import { LocalStorageService } from 'src/app/features/shared/services/LocalStorage.service';

@Component({
  selector: 'app-popup-update-virtual-location',
  templateUrl: './popup-update-virtual-location.component.html',
  styleUrls: ['./popup-update-virtual-location.component.css']
})
export class PopupUpdateVirtualLocationComponent implements OnInit, AfterViewInit {

  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;

  locationDTO: VirtualLocationDTO = {
    id: 0,
    cords: null,
    idGroup: 0,
    idSubGroup: 0,
    idLocationCategory: 0,
    cordsX: '',
    cordsY: '',
    identifier: '',
    description: '',
    locationCategory: '',
    icon: '',
    activeIcon: '',
    status: 0,
    data: [],
    idLocationCategoryStatus: 0
  };

  selectedTabIndex: number = 0;
  locationCategoryShortName: string = "";

  keyNames: DataNamesDTO[] = [];
  statusOptions: { label: string; value: number }[] = [];

  originalDataLength: number = 0;

  detailsForm: FormGroup = this.formBuilder.group({
    status: [null],
    dataValues: this.formBuilder.array([])
  });
  
  isLoading: boolean = true;
  fullScreen: boolean = false;
  mobileQuery: MediaQueryList = { matches: false } as MediaQueryList;

  subscription_GetStatuses: Subscription;
  subscription_UpdateLocation: Subscription;

  constructor(
    public dialogRef: MatDialogRef<PopupUpdateVirtualLocationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private locationCategoriesService: LocationCategoriesService,
    private virtualLocationsService: VirtualLocationsService,
    private notificationsToastService: NotificationsToastService,
    private localStorageService: LocalStorageService,
  ) {

  }

  ngOnInit() {
    this.locationDTO = this.data?.locationDTO ?? this.data ?? this.locationDTO;
    this.keyNames = this.data?.keyNames ?? [];
    this.mobileQuery = this.data?.mobileQuery ?? this.mobileQuery;

    this.detailsForm = this.formBuilder.group({
      status: [this.locationDTO.idLocationCategoryStatus],
      dataValues: this.formBuilder.array(this.locationDTO.data.map((item) => this.buildDataGroup(item))),
    });
    this.originalDataLength = this.locationDTO.data.length;

    this.getLocationStatuses();
    this.isLoading = false;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
    });
  }

  get dataValuesArray(): FormArray {
    return this.detailsForm.get('dataValues') as FormArray;
  }

  buildDataGroup(datafield: DataLocationDTO): FormGroup {
    return this.formBuilder.group({
      key: [datafield.key],
      value: [datafield.value],
      dataType: [datafield.dataType],
      idOrder: [datafield.idOrder],
    });
  }

  isNewDataField(index: number): boolean {
    return index >= this.originalDataLength;
  }

  addNewDataField() {
    const newDataField: DataLocationDTO = {
      idOrder: this.locationDTO.data.length,
      key: '',
      value: '',
      dataType: 'string'
    };
    this.locationDTO.data.push(newDataField);
    this.dataValuesArray.push(this.buildDataGroup(newDataField));
  }

  removeDataField(index: number) {
    this.locationDTO.data.splice(index, 1);
    this.dataValuesArray.removeAt(index);
  }

  isDuplicateKey(index: number): boolean {
    const key = (this.dataValuesArray.at(index).get('key').value || '').trim().toLowerCase();
    if (!key) 
      return false;

    return this.dataValuesArray.controls.some((control, i) =>
      i !== index && (control.get('key').value || '').trim().toLowerCase() === key
    );
  }

  hasDuplicateKeys(): boolean {
    return this.dataValuesArray.controls.some((data, i) => this.isDuplicateKey(i));
  }

  isEmptyKey(index: number): boolean {
    return this.isNewDataField(index) && !(this.dataValuesArray.at(index).get('key').value || '').trim();
  }

  hasEmptyNewKeys(): boolean {
    return this.dataValuesArray.controls.some((data, i) => this.isEmptyKey(i));
  }

  ngOnDestroy(): void {
    if (this.subscription_GetStatuses) {
      this.subscription_GetStatuses.unsubscribe();
    }
    if (this.subscription_UpdateLocation) {
      this.subscription_UpdateLocation.unsubscribe();
    }
  }

  getLocationStatuses(){
    this.subscription_GetStatuses = this.locationCategoriesService.getCategoryStatusList(this.locationDTO.idLocationCategory).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          const statuses = data.result;
          this.statusOptions = statuses.map((status) => ({
            label: status.locationStatusValue,
            value: status.id
          }));
          this.detailsForm.get('status')?.setValue(this.locationDTO.idLocationCategoryStatus);
          if (this.statusOptions.length > 0) {
            this.detailsForm.get('status')?.setValidators([Validators.required]);
          } else {
            this.detailsForm.get('status')?.clearValidators();
          }
          this.detailsForm.get('status')?.updateValueAndValidity();
          console.log('Estatus de categoría obtenidos:', this.statusOptions, this.locationDTO.idLocationCategoryStatus) ;
        }
      },
      error: (error) => {
        console.error('Error al obtener los estatus de categoría', error);
      }
    });
  }

  getUsableName(keyName: string): string {
    const keyNameObject = this.keyNames.find((item) => item.keyName == keyName);
    return keyNameObject ? keyNameObject.usableName : keyName;
  }

  //Obtiene la visibilidad según el nombre de la key
  getNameVisibility(keyName) {
    const keyNameObject = this.keyNames.find(
      (item) => item.keyName === keyName
    );
    return keyNameObject ? keyNameObject.isVisible : true;
  }
  private getSelectedStatusId(): number {
    const value = this.detailsForm.get('status')?.value;
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  CancelButton(){
    this.dialogRef.close();
  }

  SaveButton() {
    if (this.isLoading)
      return;
    this.detailsForm.markAllAsTouched();
    if (this.hasDuplicateKeys() || this.hasEmptyNewKeys())
      return;
    if (this.detailsForm.get('status')?.invalid)
      return;

    const locationCategories = JSON.parse(this.localStorageService.getListaCategorias() || '[]');
    const locationCategoryDTO = locationCategories.find((item: any) => item.name === this.locationDTO.locationCategory);
    if(locationCategoryDTO?.name.includes("rospect") || locationCategoryDTO?.name.includes("Prospect") || locationCategoryDTO?.isProspect){
      this.saveProspectVirtualLocation();
    } else {
      this.saveRegularVirtualLocation();
    }
  }

  buildRequestData(): VirtualDataLocationDTO[] {
    const dataValues: { key: string; value: string }[] = this.dataValuesArray.value;
    return this.locationDTO.data.map((item, index) => ({
      ...item,
      key: dataValues[index]?.key || item.key,
      value: dataValues[index]?.value ?? item.value
    }));
  }

  saveRegularVirtualLocation(){
    this.isLoading = true;
    const requestLocationDTO: VirtualLocationDTO = {
      ...this.locationDTO,
      data: this.buildRequestData(),
      status: 1,
      guid: null,
      idLocationCategoryStatus: this.getSelectedStatusId()
    };
    this.subscription_UpdateLocation = this.virtualLocationsService.createOrUpdateVirtualLocation(requestLocationDTO).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          this.dialogRef.close({
            updatedLocation: {
              ...requestLocationDTO
            },
            isUpdated: true
          });
        } else {
          this.notificationsToastService.showError('No se pudo actualizar la ubicación', 'Error al actualizar');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al actualizar la ubicación virtual', error);
        this.notificationsToastService.showError('No se pudo actualizar la ubicación', 'Error al actualizar');
        this.isLoading = false;
      }
    });
  }

  saveProspectVirtualLocation(){
    this.isLoading = true;
    const idLocationCategoryStatus: number = this.getSelectedStatusId();
    const requestLocationDTO: VirtualLocationDTO = {
      ...this.locationDTO,
      data: this.buildRequestData(),
      idLocationCategoryStatus: idLocationCategoryStatus,
      guid: null,
      status: 1
    };
    this.subscription_UpdateLocation = this.virtualLocationsService.createOrUpdateProspectLocation(requestLocationDTO, idLocationCategoryStatus).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          this.dialogRef.close({
            updatedLocation: {
              ...requestLocationDTO
            },
            isUpdated: true
          });
        } else {
          this.notificationsToastService.showError('No se pudo actualizar el prospecto', 'Error al actualizar');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.notificationsToastService.showError('No se pudo actualizar el prospecto', 'Error al actualizar');
        this.isLoading = false;
      }
    });
  }



}
